<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import wootConstants from 'dashboard/constants/globals';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnUnreadView,
} from 'dashboard/store/modules/conversations/helpers/actionHelpers';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

// No props needed as we're getting currentChat from the store directly
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);
const clearConversationDialogRef = ref(null);
const isClearingConversation = ref(false);

const currentChat = computed(() => store.getters.getSelectedChat);

const actionMenuItems = computed(() => {
  const items = [];

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-lucide-volume-off',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-lucide-volume-1',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-lucide-mail-open',
    label: t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
    action: 'mark_as_unread',
    value: 'mark_as_unread',
  });

  items.push({
    icon: 'i-lucide-share',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  if (isAdmin.value) {
    items.push({
      icon: 'i-lucide-trash-2',
      label: t('CONVERSATION.CARD_CONTEXT_MENU.CLEAR_CONVERSATION'),
      action: 'delete',
      value: 'clear_conversation',
    });
  }

  return items;
});

const actionMenuSections = computed(() => {
  if (!isAdmin.value) return [{ items: actionMenuItems.value }];

  return [
    { items: actionMenuItems.value.slice(0, -1) },
    { items: actionMenuItems.value.slice(-1) },
  ];
});

const conversationTypeForRoute = () => {
  const routeContext = { route: { name: route.name } };
  if (isOnMentionsView(routeContext)) {
    return wootConstants.CONVERSATION_TYPE.MENTION;
  }
  if (isOnParticipatingView(routeContext)) {
    return wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  }
  if (isOnUnattendedView(routeContext)) {
    return wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  if (isOnUnreadView(routeContext)) {
    return 'unread';
  }
  return '';
};

const redirectToCurrentConversationList = () => {
  const {
    accountId,
    inbox_id: inboxId,
    label,
    teamId,
    priority,
    id: customViewId,
  } = route.params;

  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationTypeForRoute(),
      customViewId,
      inboxId,
      label,
      teamId,
      priority,
    })
  );
};

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  } else if (action === 'mark_as_unread') {
    await store.dispatch('markMessagesUnread', { id: currentChat.value.id });
    redirectToCurrentConversationList();
    useAlert(t('CONTACT_PANEL.MARKED_AS_UNREAD_SUCCESS'));
  } else if (action === 'delete') {
    clearConversationDialogRef.value?.open();
  }
};

const clearConversation = async () => {
  isClearingConversation.value = true;
  try {
    await store.dispatch('clearConversationMessages', currentChat.value.id);
    clearConversationDialogRef.value?.close();
    useAlert(t('CONVERSATION.CLEAR_CONVERSATION.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.CLEAR_CONVERSATION.ERROR'));
  } finally {
    isClearingConversation.value = false;
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});
</script>

<template>
  <div class="relative flex items-center gap-2 actions--container">
    <ResolveAction
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-more-vertical"
        class="rounded-md group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        :menu-sections="actionMenuSections"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      />
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
    <Dialog
      ref="clearConversationDialogRef"
      type="alert"
      width="lg"
      :title="$t('CONVERSATION.CLEAR_CONVERSATION.TITLE')"
      :description="$t('CONVERSATION.CLEAR_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.CLEAR_CONVERSATION.CONFIRM')"
      confirm-button-icon="i-lucide-trash-2"
      :is-loading="isClearingConversation"
      @confirm="clearConversation"
    />
  </div>
</template>
